import React from 'react';
import css from './Introduction.module.scss';

/**
 * Basil v0.2, P1 and P3 — what the surface says before the writer has done anything.
 *
 * The first sentence names ONE safe action. "Any line" invited editing headings, routes and video
 * commands, every one of which this build refuses; this names the two forms that can be changed
 * here, and it names Changes and Apply changes, so the writer is not asked to guess what happens
 * between typing and the episode.
 */
export const REVIEW_AND_APPLY = 'Review Changes, then choose Apply changes to update your episode.';

export const FIRST_INSTRUCTION = `Change the words in an existing dialogue or narration line. ${REVIEW_AND_APPLY}`;

/** Said under it, because the one thing a writer can lose here is text they have not applied. */
export const CLOSING_WARNING = 'Closing Basil discards edits you have not applied.';

/** P3: what the labels in the margin are, and where the media itself is changed. */
export const MEDIA_SENTENCE = "Your episode's pictures, video and sound are shown here as labels. "
  + 'Open a scene to view or change them.';

/** The one link into the episode's own example. */
export const SHOW_AN_EXAMPLE = 'Show an example';

/** What the instruction folds into once the writer has applied a change and seen it confirmed. */
export const HOW_THIS_WORKS = 'How this works';

export type IntroductionProps = {
  /**
   * False until this browser has recorded a confirmed apply for this episode.
   *
   * While it is false the whole instruction stands. After it, one link remains: the writer who has
   * applied a change once has done the thing the instruction describes.
   */
  introductionDone: boolean;
  /**
   * P3, said ONCE: what a media label's action does when it gets there.
   *
   * Null where the episode has no labels at all, because there is then nothing to explain. It sits
   * beside the media sentence and not beside every label: four scenes would otherwise repeat it
   * four times.
   */
  exitExplanation?: string | null;
  /** Open the episode's own example: scroll to it, light it, and put the card beside it. */
  onShowExample: () => void;
};

/**
 * The instruction, the warning, the media sentence and the one link, above the writing area.
 *
 * It is not a dialog and it is not dismissible while it stands: a writer who has not applied
 * anything yet has nothing to dismiss it with, and the brief keeps it through the first confirmed
 * apply rather than through the first keystroke — two seconds is not long enough to read it.
 */
export function Introduction(props: IntroductionProps) {
  const { introductionDone, exitExplanation, onShowExample } = props;

  if (introductionDone) {
    return (
      <div className={css.folded} data-testid="basil-introduction">
        <button type="button" className={css.link} onClick={onShowExample}>{HOW_THIS_WORKS}</button>
      </div>
    );
  }

  return (
    <div className={css.introduction} data-testid="basil-introduction">
      <p className={css.instruction}>{FIRST_INSTRUCTION}</p>
      <p className={css.warning}>{CLOSING_WARNING}</p>
      <p className={css.media}>{MEDIA_SENTENCE}</p>
      {exitExplanation ? <p className={css.media}>{exitExplanation}</p> : null}
      <button type="button" className={css.example} onClick={onShowExample}>{SHOW_AN_EXAMPLE}</button>
    </div>
  );
}
